import { IFileReader, FileReaderState, EventHandler, ProgressEvent } from './types';

export class FileReader extends EventTarget implements IFileReader {
  // ReadyState constants (W3C spec compliant)
  static readonly EMPTY = 0 as const;
  static readonly LOADING = 1 as const;
  static readonly DONE = 2 as const;

  // Instance constants (required by W3C spec)
  public readonly EMPTY = 0 as const;
  public readonly LOADING = 1 as const;
  public readonly DONE = 2 as const;

  // Instance properties (readonly per W3C spec, but mutable internally)
  public error: DOMException | null = null;
  public readyState: FileReaderState = FileReader.EMPTY;
  public result: string | ArrayBuffer | null = null;

  // Event handlers (W3C spec compliant)
  public onabort: EventHandler = null;
  public onerror: EventHandler = null;
  public onload: EventHandler = null;
  public onloadend: EventHandler = null;
  public onloadstart: EventHandler = null;
  public onprogress: EventHandler = null;

  private _abortController: AbortController | null = null;

  constructor() {
    super();
  }

  // Read methods
  readAsArrayBuffer(blob: Blob): void {
    this._read(blob, 'arrayBuffer');
  }

  readAsText(blob: Blob, encoding = 'utf-8'): void {
    this._read(blob, 'text', encoding);
  }

  readAsDataURL(blob: Blob): void {
    this._read(blob, 'dataURL');
  }

  readAsBinaryString(blob: Blob): void {
    this._read(blob, 'binaryString');
  }

  abort(): void {
    if (this.readyState !== FileReader.LOADING) {
      return;
    }

    this.readyState = FileReader.DONE;
    this.result = null;
    this.error = new DOMException('The operation was aborted.', 'AbortError');

    if (this._abortController) {
      this._abortController.abort();
    }

    this._fireEvent('abort');
    this._fireEvent('loadend');
  }

  private _read(blob: Blob, format: string, encoding?: string): void {
    if (this.readyState === FileReader.LOADING) {
      throw new DOMException('The FileReader is already loading.', 'InvalidStateError');
    }

    this._performRead(blob, format, encoding);
  }

  private async _performRead(blob: Blob, format: string, encoding?: string): Promise<void> {

    this.readyState = FileReader.LOADING;
    this.result = null;
    this.error = null;
    this._abortController = new AbortController();

    this._fireEvent('loadstart');

    try {
      let totalSize = blob.size;
      let loadedSize = 0;

      // Use stream-based reading for progress events
      const reader = blob.stream().getReader();
      const chunks: Uint8Array[] = [];

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (this._abortController.signal.aborted) {
            return; // Abort was called
          }

          if (done) break;

          chunks.push(value);
          loadedSize += value.length;

          // Fire progress event (W3C spec compliant)
          const progressEvent = new Event('progress') as ProgressEvent;
          (progressEvent as any).lengthComputable = totalSize > 0;  
          (progressEvent as any).loaded = loadedSize;
          (progressEvent as any).total = totalSize;
          this._fireProgressEvent(progressEvent);
        }
      } finally {
        reader.releaseLock();
      }

      if (this._abortController.signal.aborted) {
        return; // Abort was called during reading
      }

      // Combine all chunks
      const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
      const combined = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        combined.set(chunk, offset);
        offset += chunk.length;
      }

      // Format the result based on the requested format
      switch (format) {
        case 'arrayBuffer':
          this.result = combined.buffer.slice(combined.byteOffset, combined.byteOffset + combined.byteLength);
          break;
        case 'text':
          this.result = new TextDecoder(encoding).decode(combined);
          break;
        case 'dataURL':
          const base64 = Buffer.from(combined).toString('base64');
          this.result = `data:${blob.type || 'application/octet-stream'};base64,${base64}`;
          break;
        case 'binaryString':
          this.result = Buffer.from(combined).toString('latin1');
          break;
      }

      this.readyState = FileReader.DONE;
      this._fireEvent('load');
      this._fireEvent('loadend');

    } catch (error) {
      if (this._abortController.signal.aborted) {
        return; // Abort was called
      }

      this.readyState = FileReader.DONE;
      this.error = error instanceof DOMException ? error : new DOMException(String(error), 'NotReadableError');
      this.result = null;

      this._fireEvent('error');
      this._fireEvent('loadend');
    }
  }

  private _fireEvent(type: string): void {
    const event = new Event(type);
    this.dispatchEvent(event);

    // Also call the corresponding handler if set
    const handler = (this as any)[`on${type}`];
    if (typeof handler === 'function') {
      handler.call(this, event);
    }
  }

  private _fireProgressEvent(event: ProgressEvent): void {
    this.dispatchEvent(event);

    if (typeof this.onprogress === 'function') {
      this.onprogress.call(this, event);
    }
  }
}